import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useEntityDirectorySearchApi,
  useProfileSuggestApi,
} from '@hero/api-hooks';
import type { EntityDirectoryHit } from '@hero/api';
import type { PartyRole, Profile } from '@hero/schema';
import type { EntityDirectoryPickerSelection } from '@hero/web-hooks';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';

export type { EntityDirectoryPickerSelection };

export interface EntityDirectoryPickerProps {
  /** Whether the drawer is open. Caller controls visibility. */
  isOpen: boolean;
  /** Close handler. Fires on overlay click / esc / explicit cancel. */
  onClose: () => void;
  /** Fired when the user confirms a pick or creates a suggestion. */
  onPick: (selection: EntityDirectoryPickerSelection) => void;
  /**
   * Initial query string to seed the search input. Useful when the
   * picker is opened from the Client-create flow where the user has
   * already typed the brand name.
   */
  initialQuery?: string;
  /**
   * Roles to stamp on a newly suggested Profile. Defaults to
   * `['business_customer']` because suggestions usually originate
   * from a CRM flow that's adding a customer. Consumers can pass
   * a more accurate role (e.g. `['carrier_company']` from a
   * carrier-add flow) to keep downstream filtering clean.
   */
  suggestionRoles?: PartyRole[];
  /** Optional caption rendered under the title. */
  description?: string;
}

/**
 * Unified Profile + Org picker (relations-v2). Search both the global
 * brand directory and signed-up Hero Orgs in one query; rows are
 * deduplicated server-side so a Profile linked to an Org appears as a
 * single row carrying both ids.
 *
 * When the search returns no matches, surfaces a "Suggest a new brand"
 * affordance that creates a Profile with `source: 'suggested'` so
 * admins can later promote it (or merge it into an existing entry).
 *
 * Lives in `libs/frontend/web/components/src/feature/EntityDirectory/`
 * so every dashboard (admin / carrier / customer) can reuse it. See
 * `docs/relations-v2/06-scenarios.md` C3 / C4 and the relations-v2
 * plan for the picker contract.
 */
export function EntityDirectoryPicker({
  isOpen,
  onClose,
  onPick,
  initialQuery = '',
  suggestionRoles = ['business_customer'],
  description,
}: EntityDirectoryPickerProps) {
  const { t } = useTranslation(['c-main', 'm-client']);
  const [search, setSearch] = useState(initialQuery);
  const trimmed = search.trim();

  const { data, isLoading, isFetching } = useEntityDirectorySearchApi({
    q: trimmed,
  });

  const suggestProfile = useProfileSuggestApi();

  const hits = useMemo<EntityDirectoryHit[]>(
    () => (data ?? []) as EntityDirectoryHit[],
    [data],
  );

  const onSuggestNew = async () => {
    if (!trimmed) return;
    const profile = (await suggestProfile.mutateAsync({
      name: trimmed,
      aliases: [],
      roles: suggestionRoles,
    })) as Profile;
    onPick({ createdProfile: profile });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>
            {t('m-client:modal.select_client_title', {
              defaultValue: 'Select identity',
            })}
          </SheetTitle>
          <SheetDescription>
            {description ??
              t('m-client:modal.select_client_description', {
                defaultValue:
                  'Pick a brand from the directory or a signed-up organization. Close this drawer to enter details manually.',
              })}
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 py-3 border-b">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('m-client:modal.search_client_placeholder', {
              defaultValue: 'Search brands or orgs…',
            })}
            className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {trimmed.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              {t('c-main:status.start_typing', {
                defaultValue: 'Start typing to search…',
              })}
            </div>
          ) : isLoading || isFetching ? (
            <div className="p-6 text-center text-sm text-gray-500">
              {t('c-main:async_operations.loading', { defaultValue: 'Loading…' })}
            </div>
          ) : hits.length === 0 ? (
            <div className="p-6 space-y-3 text-center">
              <div className="text-sm text-gray-500">
                {t('c-main:status.no_results_found', {
                  defaultValue: 'No matches found',
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={onSuggestNew}
                disabled={suggestProfile.isPending}>
                {suggestProfile.isPending
                  ? t('c-main:async_operations.loading', {
                      defaultValue: 'Creating…',
                    })
                  : t('c-main:actions.suggest_new', {
                      defaultValue: `Suggest "${trimmed}" as a new brand`,
                      name: trimmed,
                    })}
              </Button>
            </div>
          ) : (
            <ul className="divide-y">
              {hits.map((hit) => (
                <li key={`${hit.target}-${hit.profileId ?? hit.orgId}`}>
                  <button
                    type="button"
                    className="w-full text-start px-6 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                    onClick={() => onPick({ hit })}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">
                          {hit.name}
                        </div>
                        {hit.aliases && hit.aliases.length > 0 ? (
                          <div className="text-xs text-gray-500 truncate">
                            {hit.aliases.slice(0, 3).join(', ')}
                          </div>
                        ) : null}
                        {hit.domain ? (
                          <div className="text-xs text-gray-400 truncate">
                            {hit.domain}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-1 text-[10px] font-medium uppercase tracking-wide shrink-0">
                        <span className="rounded px-1.5 py-0.5 bg-primary/10 text-primary">
                          {hit.target === 'org'
                            ? t('m-client:drawer.peek.link_badge.org', {
                                defaultValue: 'Org',
                              })
                            : t('m-client:drawer.peek.link_badge.profile', {
                                defaultValue: 'Brand',
                              })}
                        </span>
                        {hit.isSignedUp ? (
                          <span className="rounded px-1.5 py-0.5 bg-emerald-50 text-emerald-700">
                            {t('c-main:status.signed_up', {
                              defaultValue: 'Signed up',
                            })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t p-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('c-main:cancel', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
